import { ReactNode } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import {
  Cell,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { Row } from "@tanstack/table-core";

import { NightReportFieldType } from "@modules/monitor/NightReportScreen/components/NightReportField";

import { useDataTableStore } from "./dataTableStore";
import HeaderCell from "./HeaderCell";

export type TableProps<TData> = {
  tableId: string;
  columns: ColumnDef<TData, any>[];
  data: TData[];
  getRowId?: (originalRow: TData) => string;
  onRowClick?: (originalRow: TData) => void;
  enableSorting?: boolean;
  footerText?: string;
  topContent?: ReactNode;
};

export interface VtColumnMeta {
  tooltip?: string;
  colSpan?: (row: Row<any>) => number;
  skipCell?: (row: Row<any>) => boolean;
  type: NightReportFieldType;
}

function colSpan<TData>(row: Row<TData>, cell: Cell<TData, unknown>) {
  const meta = cell.column.columnDef.meta as VtColumnMeta;
  return meta?.colSpan ? meta.colSpan(row) : 1;
}

function skipCell<TData>(row: Row<TData>, cell: Cell<TData, unknown>) {
  const meta = cell.column.columnDef.meta as VtColumnMeta;
  return meta?.skipCell ? meta.skipCell(row) : false;
}

export function DataTable<TData>({
  tableId,
  columns,
  data,
  getRowId,
  onRowClick,
  enableSorting = true,
  footerText,
  topContent,
}: TableProps<TData>) {
  const sorting = useDataTableStore((s) => s.sortingByTable[tableId]);
  const setSorting = useDataTableStore((s) => s.setSorting);

  const table = useReactTable<TData>({
    data,
    columns,
    state: { sorting },
    onSortingChange: (updater) => setSorting(tableId, updater),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId,
    enableSorting,
    // The table is unpaginated, so the automatic page-index reset is a no-op
    // state update that table-core defers to a microtask.
    autoResetPageIndex: false,
  });

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "borders.neutral.subtle",
        borderRadius: "8px",
        overflow: "hidden",
        backgroundColor: "backgrounds.static.neutral",
        width: "100%",
      }}
    >
      {!!topContent && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            minHeight: 50,
            paddingX: 2.5,
            borderBottom: "1px solid",
            borderBottomColor: "borders.neutral.subtle",
          }}
        >
          {topContent}
        </Box>
      )}

      <TableContainer
        component={Paper}
        sx={{ boxShadow: "none", borderRadius: 0 }}
      >
        <Table stickyHeader>
          <TableHead>
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((header) => (
                  <HeaderCell key={header.id} header={header} />
                ))}
              </TableRow>
            ))}
          </TableHead>

          <TableBody>
            {table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                hover={!!onRowClick}
                onClick={() => onRowClick?.(row.original)}
                sx={{ cursor: onRowClick ? "pointer" : "default", height: 48 }}
              >
                {row
                  .getVisibleCells()
                  .filter((cell) => !skipCell(row, cell))
                  .map((cell) => (
                    <TableCell
                      key={cell.id}
                      colSpan={colSpan(row, cell)}
                      sx={{
                        borderWidth: 0,
                        padding: 1.5,
                        borderTopWidth: 1,
                        borderTopStyle: "solid",
                        borderColor: "borders.neutral.subtle",
                        color: "texts.neutral.subtle",
                        height: 48,
                      }}
                    >
                      <Typography variant={"bodySmallRegular"} component="div">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </Typography>
                    </TableCell>
                  ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {!!footerText && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            minHeight: 40,
            paddingX: 2.5,
            borderTop: "2px solid",
            borderTopColor: "borders.neutral.subtle",
          }}
        >
          <Typography
            variant={"bodySmallMedium"}
            sx={{ color: "texts.neutral.subtle" }}
          >
            {footerText}
          </Typography>
        </Box>
      )}
    </Box>
  );
}
